import { useState, useEffect, useCallback, useRef } from 'react';
import { CircuitState, circuitBreakerRegistry } from '../utils/circuitBreaker';

/**
 * @module useSystemHealth
 * @description
 * Hook for monitoring system health including:
 * - Connection status (online/offline)
 * - Circuit breaker states
 * - Periodic health checks
 *
 * ## Features
 * - Pings Supabase every 60 seconds
 * - Detects offline mode after 2 consecutive failures
 * - Tracks circuit breaker states globally
 */

export interface SystemHealthState {
  /** Whether the app is online */
  isOnline: boolean;
  /** Whether we're in degraded mode (some services down) */
  isDegraded: boolean;
  /** Current circuit breaker states */
  circuitStates: Record<string, CircuitState>;
  /** Last successful health check timestamp */
  lastHealthCheck: number | null;
  /** Number of consecutive health check failures */
  consecutiveFailures: number;
}

export interface HealthCheckResult {
  healthy: boolean;
  database: boolean;
  storage: boolean;
  timestamp: string;
  latencyMs: number;
}

const PING_INTERVAL = 60000; // 60 seconds
const FAILURE_THRESHOLD = 2; // Go offline after 2 failures
const SUPABASE_HEALTH_PING_ENABLED = import.meta.env.VITE_ENABLE_SUPABASE_HEALTH_PING === 'true';

/**
 * Hook for monitoring system health
 */
export function useSystemHealth() {
  const [state, setState] = useState<SystemHealthState>({
    isOnline: true,
    isDegraded: false,
    circuitStates: {},
    lastHealthCheck: null,
    consecutiveFailures: 0,
  });

  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Perform a lightweight health check (ping)
   */
  const performHealthCheck = useCallback(async (): Promise<boolean> => {
    try {
      const startTime = performance.now();

      // Try to ping Supabase
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      if (!supabaseUrl || !SUPABASE_HEALTH_PING_ENABLED) {
        // Browser online/offline events cover default UI status. The Supabase
        // root REST endpoint returns 401 in some projects and creates noisy
        // console failures, so only ping it when explicitly enabled.
        return true;
      }

      // HEAD request is lighter than GET
      const response = await fetch(`${supabaseUrl}/rest/v1/`, {
        method: 'HEAD',
        headers: {
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || '',
        },
        // Short timeout for health check
        signal: AbortSignal.timeout(5000),
      });

      const latencyMs = performance.now() - startTime;
      if (import.meta.env.DEV) console.debug(`[HealthCheck] Ping successful (${latencyMs.toFixed(0)}ms)`);

      return response.ok || response.status === 400 || response.status === 401;
    } catch (error) {
      if (import.meta.env.DEV) console.warn('[HealthCheck] Ping failed:', error);
      return false;
    }
  }, []);

  /**
   * Update state based on health check result
   */
  const handleHealthCheckResult = useCallback((success: boolean) => {
    setState(prev => {
      if (success) {
        // Health check succeeded
        return {
          ...prev,
          isOnline: true,
          lastHealthCheck: Date.now(),
          consecutiveFailures: 0,
        };
      } else {
        // Health check failed
        const newFailures = prev.consecutiveFailures + 1;
        const shouldGoOffline = newFailures >= FAILURE_THRESHOLD;

        if (shouldGoOffline && prev.isOnline) {
          if (import.meta.env.DEV) console.warn('[HealthCheck] Going OFFLINE after consecutive failures');
        }

        return {
          ...prev,
          isOnline: !shouldGoOffline,
          consecutiveFailures: newFailures,
        };
      }
    });
  }, []);

  /**
   * Manually trigger a health check
   */
  const checkNow = useCallback(async () => {
    const result = await performHealthCheck();
    handleHealthCheckResult(result);
    return result;
  }, [performHealthCheck, handleHealthCheckResult]);

  /**
   * Subscribe to circuit breaker state changes
   */
  useEffect(() => {
    const unsubscribe = circuitBreakerRegistry.onStateChange((name, circuitState) => {
      setState(prev => ({
        ...prev,
        circuitStates: {
          ...prev.circuitStates,
          [name]: circuitState,
        },
        isDegraded: circuitBreakerRegistry.hasOpenCircuits(),
      }));
    });

    // Initialize circuit states
    setState(prev => ({
      ...prev,
      circuitStates: circuitBreakerRegistry.getAllStates(),
      isDegraded: circuitBreakerRegistry.hasOpenCircuits(),
    }));

    return unsubscribe;
  }, []);

  /**
   * Setup periodic health checks
   */
  useEffect(() => {
    // Perform initial health check
    checkNow();

    // Setup interval for periodic checks
    pingIntervalRef.current = setInterval(checkNow, PING_INTERVAL);

    // Listen for online/offline browser events
    const handleOnline = () => {
      if (import.meta.env.DEV) console.info('[HealthCheck] Browser reports ONLINE');
      checkNow();
    };

    const handleOffline = () => {
      if (import.meta.env.DEV) console.warn('[HealthCheck] Browser reports OFFLINE');
      setState(prev => ({
        ...prev,
        isOnline: false,
        consecutiveFailures: FAILURE_THRESHOLD,
      }));
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [checkNow]);

  return {
    ...state,
    checkNow,
  };
}

/**
 * Get health indicator props based on system state
 */
export type HealthIndicatorColor = 'green' | 'yellow' | 'red';

export interface HealthIndicatorProps {
  color: HealthIndicatorColor;
  label: string;
  pulse: boolean;
  tooltip: string;
}

export function getHealthIndicatorProps(state: SystemHealthState): HealthIndicatorProps {
  if (!state.isOnline) {
    return {
      color: 'red',
      label: 'Sin conexión',
      pulse: true,
      tooltip: 'Sin conexión a internet. Los cambios se guardarán localmente.',
    };
  }

  if (state.isDegraded) {
    const openCircuits = Object.entries(state.circuitStates)
      .filter(([_, s]) => s === 'OPEN')
      .map(([name]) => name);

    return {
      color: 'yellow',
      label: 'Degradado',
      pulse: true,
      tooltip: `Servicios afectados: ${openCircuits.join(', ')}`,
    };
  }

  const halfOpenCircuits = Object.entries(state.circuitStates)
    .filter(([_, s]) => s === 'HALF_OPEN')
    .map(([name]) => name);

  if (halfOpenCircuits.length > 0) {
    return {
      color: 'yellow',
      label: 'Recuperando',
      pulse: true,
      tooltip: `Recuperando conexión: ${halfOpenCircuits.join(', ')}`,
    };
  }

  return {
    color: 'green',
    label: 'En línea',
    pulse: false,
    tooltip: 'Todos los sistemas operativos',
  };
}

export default useSystemHealth;
